import React, { useState, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import "typeface-poppins";
import "typeface-raleway";
import { keyframes } from '@mui/system';

const shine = keyframes`
  0% {
    background-position: -200% center;
  }
  100% {
    background-position: 200% center;
  }
`;

// Define card sizes and positions
const getCardGradient = (number) => {
  const gradients = {
    "1": "linear-gradient(to right, #A47300, #FCB100)",
    "2": "linear-gradient(to right, #006B3D, #00D17B)",
    "3": "linear-gradient(to right, #8B0000, #FF4040)",
    "4": "linear-gradient(to right, #4B0082, #9370DB)",
    "5": "linear-gradient(to right, #004D40, #00BFA5)",
    "6": "linear-gradient(to right, #1A237E, #3F51B5)",
    "7": "linear-gradient(to right, #1B5E20, #4CAF50)",
    "8": "linear-gradient(to right, #0D47A1, #2196F3)",
    "9": "linear-gradient(to right, #311B92, #673AB7)",
    "10": "linear-gradient(to right, #B71C1C, #F44336)",
    "11": "linear-gradient(to right, #01579B, #03A9F4)",
    "12": "linear-gradient(to right, #FF6F00, #FFC107)",
    "13": "linear-gradient(to right, #BF360C, #FF5722)",
    "14": "linear-gradient(to right, #880E4F, #E91E63)",
    "15": "linear-gradient(to right, #4A148C, #9C27B0)",
    "16": "linear-gradient(to right, #006064, #00BCD4)",
    "17": "linear-gradient(to right, #3E2723, #795548)"
  };
  return gradients[number] || "transparent";
};

const getTextGradient = (number) => {
  const gradients = {
    "1": "linear-gradient(90deg, #000000, #FFE0B2)",
    "2": "linear-gradient(90deg, #000000, #B2DFDB)",
    "3": "linear-gradient(90deg, #000000, #FFCDD2)",
    "4": "linear-gradient(90deg, #000000, #E1BEE7)",
    "5": "linear-gradient(90deg, #000000, #B2DFDB)",
    "6": "linear-gradient(90deg, #000000, #C5CAE9)",
    "7": "linear-gradient(90deg, #000000, #C8E6C9)",
    "8": "linear-gradient(90deg, #000000, #BBDEFB)",
    "9": "linear-gradient(90deg, #000000, #D1C4E9)",
    "10": "linear-gradient(90deg, #000000, #FFCDD2)",
    "11": "linear-gradient(90deg, #000000, #B3E5FC)",
    "12": "linear-gradient(90deg, #000000, #FFE0B2)",
    "13": "linear-gradient(90deg, #000000, #FFCCBC)",
    "14": "linear-gradient(90deg, #000000, #F8BBD0)",
    "15": "linear-gradient(90deg, #000000, #E1BEE7)",
    "16": "linear-gradient(90deg, #000000, #B2EBF2)",
    "17": "linear-gradient(90deg, #000000, #D7CCC8)"
  };
  return gradients[number] || "transparent";
};

const cardLayouts = [
  // First row - 4 cards (centered)
  { number: "1", title: "Affordable and clean energy", gridColumn: "1 / span 1", marginLeft: "80px" },
  { number: "2", title: "Industry, innovation and infrastructure", gridColumn: "2 / span 1", marginLeft: "80px" },
  { number: "3", title: "Climate Action", gridColumn: "3 / span 1", marginLeft: "80px" },
  { number: "4", title: "Sustainable cities communities", gridColumn: "4 / span 1", marginLeft: "80px" },
  // Second row - 5 cards
  { number: "5", title: "Responsible consumption and production", gridColumn: "1 / span 1", marginLeft: "0", width: "240px" },
  { number: "6", title: "Decent Work And Economic Growth", gridColumn: "2 / span 1", marginLeft: "-20px", width: "240px" },
  { number: "7", title: "Life on Land", gridColumn: "3 / span 1", marginLeft: "-40px", width: "240px" },
  { number: "8", title: "Life Below Water", gridColumn: "4 / span 1", marginLeft: "-60px", width: "240px" },
  { number: "9", title: "Peace, Justice And Strong Institutions", gridColumn: "5 / span 1", marginLeft: "-80px", width: "240px" },
  // Third row - 4 cards
  { number: "10", title: "Good Health And Well-Being", gridColumn: "1 / span 1",marginLeft: "80px" },
  { number: "11", title: "Clean Water and Sanitation", gridColumn: "2 / span 1" ,marginLeft: "80px"},
  { number: "12", title: "Zero Hunger", gridColumn: "3 / span 1",marginLeft: "80px" },
  { number: "13", title: "No Poverty", gridColumn: "4 / span 1",marginLeft: "80px" },
  // Fourth row - 4 cards
  { number: "14", title: "Quality Education", gridColumn: "1 / span 1" },
  { number: "15", title: "Gender Equality", gridColumn: "2 / span 1" },
  { number: "16", title: "Reduced Inequalities", gridColumn: "3 / span 1" },
  { number: "17", title: "Partnerships For The Goals", gridColumn: "4 / span 1" },
];

const Page3 = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [hoveredCardIndex, setHoveredCardIndex] = useState(null);

  useEffect(() => {
    const handleScroll = () => {
      const element = document.getElementById('page3');
      if (element) {
        const position = element.getBoundingClientRect();
        // If element is in viewport
        if (position.top < window.innerHeight && position.bottom >= 0) {
          setIsVisible(true);
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Check initial position

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleMouseOver = (index) => {
    setHoveredCardIndex(index);
  };

  const handleMouseOut = () => {
    setHoveredCardIndex(null);
  };

  return (
    <Box
      id="page3"
      sx={{
        backgroundColor: "black",
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: "2rem",
        position: "relative",
      }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(5, 260px)',
          maxWidth: '1300px',
          width: 'fit-content',
          gridAutoRows: '174px',
          margin: '0 auto',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {cardLayouts.map((card, index) => (
          <Box
            key={card.number}
            sx={{
              width: card.width || '260px',
              height: '174px',
              borderLeft: card.number === "1" || card.number === "5" || card.number === "10" || card.number === "14" ? "none" : "0.1px solid #CB8B67",
              borderRight: card.number === "4" || card.number === "13" || card.number === "17" ? "none" : "0.1px solid #CB8B67",
              borderBottom: card.number === "17" || card.number === "14" || card.number === "15" || card.number === "16" ? "none" : "0.1px solid #CB8B67",
              padding: "20px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              gridColumn: card.gridColumn,
              marginRight: card.marginRight || '0',
              marginLeft: card.marginLeft || '0',
              boxSizing: 'border-box',
              position: 'relative',
              overflow: 'hidden',
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
              transition: `all 0.6s ease-out ${index * 0.2}s`,
              '&::after': {
                content: '""',
                position: 'absolute',
                top: '-50%',
                left: '-50%',
                width: '200%',
                height: '200%',
                background: 'linear-gradient(45deg, transparent 45%, rgba(255,255,255,0.1) 48%, rgba(255,255,255,0.2) 50%, rgba(255,255,255,0.1) 52%, transparent 55%)',
                backgroundSize: '200% 200%',
                opacity: 0,
                transition: 'opacity 0.3s ease',
                animation: `${shine} 2s infinite linear`,
                pointerEvents: 'none'
              },
              '&:hover': {
                background: getCardGradient(card.number),
                '&::after': {
                  opacity: 1
                }
              }
            }}
            onMouseOver={() => handleMouseOver(index)}
            onMouseOut={handleMouseOut}
          >
            <Typography
              sx={{
                color: "transparent",
                fontFamily: "Poppins",
                fontSize: "48px",
                fontWeight: "bold",
                WebkitTextStroke: "1px #CB8B67",
                textStroke: "1px #CB8B67",
                position: "relative",
                transition: 'all 0.3s ease',
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
                transition: `all 0.6s ease-out ${index * 0.2 + 0.2}s`,
                '.MuiBox-root:hover &': {
                  color: '#000000',
                  WebkitTextStroke: 'none',
                  textStroke: 'none'
                }
              }}
            >
              {card.number}
            </Typography>
            <Typography
              sx={{
                color: "#4F4F4F",
                fontFamily: "Poppins",
                fontSize: "20px",
                fontWeight: "bold",
                transition: 'all 0.3s ease',
                WebkitTextFillColor: hoveredCardIndex === index ? 'transparent' : '#4F4F4F',
                backgroundImage: hoveredCardIndex === index ? getTextGradient(card.number) : 'none',
                marginTop: '-15px',
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
                transition: `all 0.6s ease-out ${index * 0.2 + 0.4}s`
              }}
            >
              {card.title}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default Page3;
